import React, { useId } from "react";
import { Button, ButtonProps } from "@mui/material";

interface FlashyButtonProps extends ButtonProps {
  flashColors?: [string, string]; // [color1, color2] for the flash gradient
  borderColor?: string;
  textColor?: string;
  pulseInterval?: number; // Pulse animation interval in seconds
  flashInterval?: number; // Flash animation interval in seconds
  flashOpacity?: number; // Flash opacity (0-1)
  flashWidth?: string; // Flash width as percentage
}

/**
 * FlashyButton - Reusable button with pulsing border and left-to-right flash animations
 *
 * Keyframes use a per-instance id so global stylesheets cannot override `@keyframes pulse`.
 */
const FlashyButton: React.FC<FlashyButtonProps> = ({
  children,
  flashColors = ["#ff69b4", "#9966cc"], // Default pink to purple
  borderColor = "purple",
  textColor = "black",
  pulseInterval = 2,
  flashInterval = 2,
  flashOpacity = 0.5,
  flashWidth = "80%",
  sx,
  ...buttonProps
}) => {
  const [color1, color2] = flashColors;
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "_");
  const pulseKf = `cmFlashyPulse_${uid}`;
  const sweepKf = `cmFlashySweep_${uid}`;

  return (
    <Button
      {...buttonProps}
      sx={{
        borderColor: borderColor,
        color: textColor,
        position: "relative",
        overflow: "hidden",
        fontWeight: "bold",
        fontSize: "0.9rem",
        px: 3,
        py: 1.2,
        [`@keyframes ${pulseKf}`]: {
          "0%": {
            transform: "scale(1)",
            opacity: 1,
            borderColor: borderColor
          },
          "50%": {
            transform: "scale(1.05)",
            opacity: 0.8,
            borderColor: `rgba(128, 0, 128, 0.5)` // TODO: derive from borderColor
          },
          "100%": {
            transform: "scale(1)",
            opacity: 1,
            borderColor: borderColor
          }
        },
        [`@keyframes ${sweepKf}`]: {
          "0%": {
            transform: "translateX(-100%)",
            opacity: 0
          },
          "30%": {
            opacity: flashOpacity
          },
          "70%": {
            opacity: flashOpacity
          },
          "100%": {
            transform: "translateX(100%)",
            opacity: 0
          }
        },
        animation: `${pulseKf} ${pulseInterval}s ease-in-out infinite`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: flashWidth,
          height: "100%",
          background: `linear-gradient(90deg, transparent, ${color1} 30%, ${color2} 70%, transparent)`,
          animation: `${sweepKf} ${flashInterval}s ease-in-out infinite`,
          zIndex: 1,
          pointerEvents: "none"
        },
        "&:hover": {
          borderColor: borderColor,
          backgroundColor: `rgba(128, 0, 128, 0.1)`,
          animation: `${pulseKf} ${pulseInterval / 2}s ease-in-out infinite`,
          "&::before": {
            animation: `${sweepKf} ${flashInterval / 2}s ease-in-out infinite`
          }
        },
        "& span": {
          position: "relative",
          zIndex: 2
        },
        ...sx
      }}
    >
      {children}
    </Button>
  );
};

export default FlashyButton;
